import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { companyKeys } from '@/hooks/api/query-keys';

// ============ Types ============

export interface CompanyProfile {
  id: string;
  name: string;
  slug: string;
  industry: string | null;
  companySize: string | null;
  website: string | null;
  address: string | null;
  /**
   * ISO-3166-1 alpha-2, uppercase. The API has returned and accepted this since
   * 2026-04-19; it was simply missing from this interface, so `tsc` rejected
   * every attempt to read it and no editor was ever built. Onboarding captured
   * a country, and nothing could show or change it afterwards.
   */
  country: string | null;
  description: string | null;
  timezone: string | null;
  logoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCompanyProfileDto {
  name?: string;
  slug?: string;
  industry?: string;
  companySize?: string;
  website?: string;
  address?: string;
  /** ISO-3166-1 alpha-2. The API upper-cases and truncates to 2 chars. */
  country?: string;
  description?: string;
  timezone?: string;
  logoUrl?: string;
}

export interface PasswordPolicy {
  minLength?: number;
  requireUppercase?: boolean;
  requireNumber?: boolean;
  requireSpecial?: boolean;
}

// Mirrors CompanySettingsResponseDto. The previous shape
// (defaultRecipientStatus/autoDetectDomain/displayNameFormat) never existed on the
// API — posting it would have 400'd on forbidNonWhitelisted.
export interface CompanySettings {
  id: string;
  companyId: string;
  timezone: string | null;
  requireTwoFactor: boolean;
  passwordPolicy: PasswordPolicy | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCompanySettingsDto {
  timezone?: string;
  requireTwoFactor?: boolean;
  passwordPolicy?: PasswordPolicy;
}

// ============ Query Hooks ============

/**
 * GET /v1/companies/profile checks read:Company, which only the company tier
 * holds. A page a workspace-tier role can open passes `enabled` from its gate
 * (`!gate.loading && !gate.denied`) so the request is never made only to 403
 * (browser QA 2026-10-06: Workspace settings and Compliance).
 */
export function useCompanyProfile(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: companyKeys.profile(),
    queryFn: () => fetchApi<CompanyProfile>('/companies/profile'),
    enabled: options.enabled ?? true,
  });
}

export function useCompanySettings() {
  return useQuery({
    queryKey: companyKeys.settings(),
    queryFn: () => fetchApi<CompanySettings>('/companies/settings'),
  });
}

// ============ Mutation Hooks ============

export function useUpdateCompanyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCompanyProfileDto) =>
      fetchApi<CompanyProfile>('/companies/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(companyKeys.profile(), updated);
    },
  });
}

export function useUpdateCompanySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCompanySettingsDto) =>
      fetchApi<CompanySettings>('/companies/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(companyKeys.settings(), updated);
    },
  });
}
